import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';

/**
 * Bulk stock transfer — one trip between two sites, several materials on it.
 *
 * "TN 09 BX 4471 left Ramesh Residence for Dharani Villa with 40 bags cement
 * and 2 bags M-Sand" is one trip. The date, both sites, the vehicle and the
 * driver are shared; only the materials differ.
 *
 * Each material line can record the unit transfer rate (rateRupees) and its
 * calculated transfer value (quantity * rate), reflecting changing daily costs.
 *
 * Written by a single database function so it is one transaction.
 */

const MONEY = /^\d+(\.\d{1,2})?$/;
const cleanMoney = (s: string) => s.replace(/[₹,\s]/g, '');

export interface TransferLine {
  material_id: string;
  unit_id: string;
  /** Kept as a string so a half-typed "1." does not become NaN mid-keystroke. */
  quantity: string;
  /** Manually entered unit rate in Rupees (e.g. "450" or "42.50"). */
  rateRupees: string;
  remarks: string;
}

export const emptyTransferLine = (): TransferLine => ({
  material_id: '',
  unit_id: '',
  quantity: '',
  rateRupees: '',
  remarks: '',
});

export interface TransferDraft {
  date: string;
  from_project_id: string;
  to_project_id: string;
  vehicle_no: string;
  driver_name: string;
  remarks: string;
  lines: TransferLine[];
}

/** Total monetary value of a single transfer line in paise. */
export function transferLineTotalPaise(l: TransferLine): number | null {
  const qty = Number(l.quantity);
  const cleanRate = cleanMoney(l.rateRupees || '');
  const rate = Number(cleanRate);
  if (!Number.isFinite(qty) || qty <= 0 || !cleanRate || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }
  return Math.round(qty * rate * 100);
}

/** Total monetary sum across all filled transfer lines in paise. */
export function transferLinesTotalPaise(lines: TransferLine[]): number {
  return lines.reduce((sum, l) => sum + (transferLineTotalPaise(l) ?? 0), 0);
}

/** True once a line has enough in it to be worth saving. */
export function transferLineFilled(l: TransferLine): boolean {
  const qty = Number(l.quantity);
  return !!l.material_id && !!l.quantity.trim() && Number.isFinite(qty) && qty > 0;
}

export function filledTransferCount(lines: TransferLine[]): number {
  return lines.filter(transferLineFilled).length;
}

/* ------------------------------ stock query ------------------------------ */

export function useProjectStock(projectId?: string) {
  return useQuery({
    queryKey: ['project-stock-map', projectId],
    enabled: !!projectId,
    queryFn: async (): Promise<Map<string, number>> => {
      if (!projectId) return new Map();
      const [purchases, inTransfers, usage, outTransfers] = await Promise.all([
        supabase
          .from('material_purchases')
          .select('material_id, quantity')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
        supabase
          .from('stock_transfers')
          .select('material_id, quantity')
          .eq('to_project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
        supabase
          .from('material_usage')
          .select('material_id, quantity')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
        supabase
          .from('stock_transfers')
          .select('material_id, quantity')
          .eq('from_project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
      ]);

      const stock = new Map<string, number>();
      for (const p of purchases.data ?? []) {
        stock.set(p.material_id, (stock.get(p.material_id) ?? 0) + Number(p.quantity));
      }
      for (const t of inTransfers.data ?? []) {
        stock.set(t.material_id, (stock.get(t.material_id) ?? 0) + Number(t.quantity));
      }
      for (const u of usage.data ?? []) {
        stock.set(u.material_id, (stock.get(u.material_id) ?? 0) - Number(u.quantity));
      }
      for (const t of outTransfers.data ?? []) {
        stock.set(t.material_id, (stock.get(t.material_id) ?? 0) - Number(t.quantity));
      }
      return stock;
    },
  });
}

/* ------------------------------ validation ------------------------------ */

export interface TransferErrors {
  from_project_id?: string;
  to_project_id?: string;
  /** Keyed by line index. */
  lines: Record<number, Partial<Record<keyof TransferLine, string>>>;
  /** Something wrong with the trip as a whole. */
  form?: string;
}

export function validateTransfer(draft: TransferDraft, stockMap?: Map<string, number>): TransferErrors {
  const e: TransferErrors = { lines: {} };
  if (!draft.from_project_id) e.from_project_id = 'Choose the sending site';
  if (!draft.to_project_id) e.to_project_id = 'Choose the receiving site';
  else if (draft.to_project_id === draft.from_project_id) e.to_project_id = 'Choose a different site';

  const touchedRows = draft.lines.filter((l) => l.material_id || l.quantity.trim() || l.rateRupees.trim());
  if (!touchedRows.length) e.form = 'Add at least one material';

  const seen = new Map<string, number>();
  draft.lines.forEach((l, i) => {
    // A blank spare row is not an error — three are offered by default.
    if (!l.material_id && !l.quantity.trim() && !l.rateRupees.trim()) return;
    const le: Partial<Record<keyof TransferLine, string>> = {};

    if (!l.material_id) le.material_id = 'Choose a material';
    else {
      const first = seen.get(l.material_id);
      if (first !== undefined) le.material_id = `Same as line ${first + 1}`;
      else seen.set(l.material_id, i);
    }

    const qty = Number(l.quantity);
    if (!l.quantity.trim() || !Number.isFinite(qty) || qty <= 0) {
      le.quantity = 'Enter a quantity';
    } else if (l.material_id && stockMap) {
      const available = stockMap.get(l.material_id) ?? 0;
      if (available <= 0) {
        le.quantity = 'No stock available on site (0)';
      } else if (qty > available) {
        le.quantity = `Exceeds available stock (Max: ${available})`;
      }
    }

    const cleanRate = cleanMoney(l.rateRupees || '');
    if (cleanRate && (!MONEY.test(cleanRate) || Number(cleanRate) <= 0)) {
      le.rateRupees = 'Enter a valid rate';
    }

    if (Object.keys(le).length) e.lines[i] = le;
  });

  return e;
}

export function transferHasErrors(e: TransferErrors): boolean {
  return !!(e.from_project_id || e.to_project_id || e.form || Object.keys(e.lines).length);
}

/* -------------------------------- writing -------------------------------- */

export function useCreateTransferBatch() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (draft: TransferDraft): Promise<string> => {
      const errors = validateTransfer(draft);
      if (transferHasErrors(errors)) throw new Error(errors.form ?? 'Check the highlighted fields');

      const lines = draft.lines.filter(transferLineFilled).map((l) => {
        const cleanRate = cleanMoney(l.rateRupees || '');
        const ratePaise = cleanRate && Number(cleanRate) > 0 ? Math.round(Number(cleanRate) * 100) : null;
        return {
          material_id: l.material_id,
          unit_id: l.unit_id || null,
          quantity: Number(l.quantity),
          rate_paise: ratePaise,
          remarks: l.remarks.trim() || null,
        };
      });

      const { data, error } = await supabase.rpc('create_stock_transfer_batch', {
        p_from_project_id: draft.from_project_id,
        p_to_project_id: draft.to_project_id,
        p_transferred_on: draft.date,
        p_lines: lines,
        p_vehicle_no: draft.vehicle_no.trim() || undefined,
        p_driver_name: draft.driver_name.trim() || undefined,
        p_remarks: draft.remarks.trim() || undefined,
      });
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: (_data, draft) => {
      void qc.invalidateQueries({ queryKey: ['stock-transfers'] });
      for (const projectId of [draft.from_project_id, draft.to_project_id]) {
        void qc.invalidateQueries({ queryKey: ['project', projectId] });
        void qc.invalidateQueries({ queryKey: ['project-stock-map', projectId] });
      }
      void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['client'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['my-submissions'] });
      void qc.invalidateQueries({ queryKey: ['materials'] });
      void qc.invalidateQueries({ queryKey: ['material'] });
      void qc.invalidateQueries({ queryKey: ['material-full-report'] });
      void qc.invalidateQueries({ queryKey: ['material-report-picker'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['staff-dashboard'] });
    },
  });
}
